import { request } from '@umijs/max';
import { message } from 'antd';
import locale from '../locales/en-US';
import type { SharePointSource } from './microsoftIntegration.service';

const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// Group APIs
export async function getGroups() {
  return request(`/api/files/groups/`, { method: 'GET' });
}

export async function createGroup(data: any) {
  return request(`/api/files/groups/`, {
    method: 'POST',
    data,
  });
}

export async function updateGroup(groupId: string, data: any) {
  return request(`/api/files/groups/${groupId}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteGroup(groupId: string) {
  return request(`/api/files/groups/${groupId}`, { method: 'DELETE' });
}

// File APIs
export async function getFiles(rootFolderId: string | undefined, params: any) {
  const url = rootFolderId ? `/api/files/${rootFolderId}` : '/api/files/';
  return request(url, {
    method: 'GET',
    params,
  });
}

export async function getFileHistory(rootFolderId: string) {
  return request(`/api/files/history/${rootFolderId}`, { method: 'GET' });
}

export async function createFolder(data: { folderId?: string; name: string }) {
  return request(`/api/files/folder/`, {
    method: 'POST',
    data,
  });
}

export async function uploadFilesToFolder(data: {
  folder_id: string;
  file_urls: string[];
  bulk?: boolean;
  /** Parallel to file_urls; null for files that did not come from SharePoint. */
  sources?: (SharePointSource | null)[];
}) {
  return request(`/api/files/`, {
    method: 'POST',
    data,
  });
}

/** Index an uploaded bucket file as a hidden (folderless) context document. */
export async function createTransientFile(fileUrl: string) {
  return request(`/api/files/transient`, {
    method: 'POST',
    data: { file_url: fileUrl },
  });
}

export async function getFileById(id: string) {
  return request(`/api/files/${id}`, { method: 'GET' });
}

export async function updateFileName(id: string, name: string) {
  return request(`/api/files/${id}`, {
    method: 'PUT',
    data: { name },
  });
}

export async function updateFileTag(id: string, userTags: string[]) {
  return request(`/api/files/${id}`, {
    method: 'PUT',
    data: { userTags },
  });
}

export async function updateFolderName(id: string, name: string) {
  return request(`/api/files/folder/${id}`, {
    method: 'PUT',
    data: { name },
  });
}

export async function deleteFile(id: string) {
  return request(`/api/files/${id}`, { method: 'DELETE' });
}

export async function moveFile(fileId: string, folderId: string) {
  return request(`/api/files/${fileId}`, {
    method: 'PUT',
    data: { folderId },
  });
}

export async function bulkMoveFiles(fileIds: string[], folderId: string) {
  return request(`/api/files/bulk-move`, {
    method: 'PUT',
    data: { fileIds, folderId },
  });
}

export async function bulkDeleteFiles(fileIds: string[]) {
  return request(`/api/files/bulk-delete`, {
    method: 'DELETE',
    data: { fileIds },
  });
}

// Sort Preference APIs
export async function getFilesSortOrder() {
  return request('/api/user/preferences/files-sort-order', { method: 'GET' });
}

export async function saveFilesSortOrder(sortOrder: string | null) {
  return request('/api/user/preferences/files-sort-order', {
    method: 'PUT',
    data: { sortOrder },
  });
}

export async function getFilesViewMode() {
  return request('/api/user/preferences/files-view-mode', { method: 'GET' });
}

export async function saveFilesViewMode(viewMode: string) {
  return request('/api/user/preferences/files-view-mode', {
    method: 'PUT',
    data: { viewMode },
  });
}

export async function getGroupsSortOrder() {
  return request('/api/user/preferences/groups-sort-order', { method: 'GET' });
}

export async function saveGroupsSortOrder(sortOrder: string | null) {
  return request('/api/user/preferences/groups-sort-order', {
    method: 'PUT',
    data: { sortOrder },
  });
}

// Helper APIs
export async function getEmployees() {
  return request(`/api/files/employees/`);
}

export async function getTags() {
  return request(`/api/files/allowed-tags/`);
}

export async function fetchFavorites() {
  return request(`/api/files/favorites/`);
}

export async function favoriteClick(fileId: string) {
  return request(`/api/files/favorite/${fileId}`, { method: 'POST' });
}

export const downloadFolder = async (
  folderId: string,
  folderName: string,
): Promise<'success' | 'empty'> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Authorization token not found');

    const response = await fetch(`/api/files/download-folder-zip/?folderId=${folderId}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 404) {
      const errorResponse = await response.json();
      if (errorResponse.detail === 'Folder is empty or not found.') {
        return 'empty';
      }
    }

    if (!response.ok) throw new Error('Network error while downloading the folder');

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `${folderName}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(downloadUrl);
    return 'success';
  } catch (error) {
    if (error instanceof Error && error.message === 'Authorization token not found') {
      message.error(locale['pages.file.prompt.authFail']);
    } else {
      message.error(locale['pages.file.prompt.downloadFail']);
    }
    throw error;
  }
};

export const toggleFavoriteFile = async (serverUrl: string, file_id: string) => {
  try {
    const response = await request(`${serverUrl}/files/favorite/${file_id}`, {
      method: 'POST',
      headers: getHeaders(),
    });

    if (response.success) {
      return { success: true, message: response.message };
    }
    return { success: false, message: 'Unexpected response from server.' };
  } catch (error: any) {
    const status = error.response?.status;

    let errorMessage = locale['pages.file.prompt.addFavoriteFail'];
    if (status === 404) {
      errorMessage = locale['pages.file.prompt.notFound'];
    } else if (status === 403) {
      errorMessage = locale['pages.file.prompt.noPermission'];
    }
    throw new Error(errorMessage);
  }
};

export type DownloadFormat = 'xlsx' | 'docx';
export type DownloadView = 'summary' | 'detailed';
export type SelectedReviewCell = { rowId?: string; colId?: string };

// download() derives the saved filename from the last path segment of the
// signed file URL. That URL is frequently a presigned S3/GCS link carrying a
// long query string (signature, expiry, credential, ...) - naively taking
// everything after the last "/" then includes that query string, so the
// file gets saved with the signature blob as its name. Using URL.pathname
// strips the query string before we take the last segment.
function extractDownloadFilename(url: string, fallback: string): string {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const base = decodeURIComponent(pathname.split('/').pop() ?? '');
    return base || fallback;
  } catch {
    return fallback;
  }
}

const fetchDownloadUrl = async (component: string, url: string) => {
  const token = localStorage.getItem('token');
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/docx',
      Authorization: `Bearer ${token}`,
    },
  });
  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.setAttribute(
    'download',
    extractDownloadFilename(url, `${component}_${url.split('/').pop()}`),
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

/** Prepare an export for reuse in another feature without saving it locally. */
export async function getExportUrl(
  component: string,
  docId: string,
  format: DownloadFormat = 'xlsx',
  options?: { view?: DownloadView },
): Promise<string | undefined> {
  let suffix = '';
  if (component === 'review') {
    suffix = format === 'docx' ? '/docx' : '/excel';
  }
  const viewQuery = component === 'review' && options?.view ? `?view=${options.view}` : '';
  const res = await request(`/api/download/${component}/${docId}${suffix}${viewQuery}`);
  if (res.url) return res.url;
  message.error(`${component} not downloadable`);
  return undefined;
}

export async function download(
  component: string,
  docId: string,
  format: DownloadFormat = 'xlsx',
  options?: { view?: DownloadView },
): Promise<string | undefined> {
  window.dispatchEvent(new CustomEvent('download-start'));
  try {
    const url = await getExportUrl(component, docId, format, options);
    if (url) await fetchDownloadUrl(component, url);
    return url;
  } finally {
    window.dispatchEvent(new CustomEvent('download-end'));
  }
}

export async function downloadComparisonExcel(
  docId: string,
  comparisonView: import('./comparison.service').ComparisonView,
): Promise<string | undefined> {
  window.dispatchEvent(new CustomEvent('download-start'));
  try {
    const res = await request(`/api/download/comparison/${docId}/excel`, {
      method: 'POST',
      data: comparisonView,
    });
    if (res.success && res.url) {
      await fetchDownloadUrl('comparison', res.url);
      return res.url;
    }
    message.error('Comparison not downloadable');
    return undefined;
  } finally {
    window.dispatchEvent(new CustomEvent('download-end'));
  }
}

/** Prepare only the selected Review cells, preserving the chosen view. */
export async function getSelectedReviewCellsExportUrl(
  docId: string,
  options: { view?: DownloadView; cells: SelectedReviewCell[] },
  format: DownloadFormat = 'xlsx',
): Promise<string | undefined> {
  const suffix = format === 'docx' ? '/docx' : '/excel';
  const res = await request(`/api/download/review/${docId}${suffix}/selected`, {
    method: 'POST',
    data: {
      view: options.view ?? 'summary',
      cells: options.cells,
    },
  });
  if (res.url) return res.url;
  message.error('review not downloadable');
  return undefined;
}

export async function downloadSelectedReviewCells(
  docId: string,
  options: { view?: DownloadView; cells: SelectedReviewCell[] },
  format: DownloadFormat = 'xlsx',
): Promise<string | undefined> {
  window.dispatchEvent(new CustomEvent('download-start'));
  try {
    const url = await getSelectedReviewCellsExportUrl(docId, options, format);
    if (url) await fetchDownloadUrl('review', url);
    return url;
  } finally {
    window.dispatchEvent(new CustomEvent('download-end'));
  }
}
